<template>
  <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
    <button type="button" @click="open = !open"
      class="w-full flex items-center justify-between gap-3 px-5 py-3.5 text-left hover:bg-gray-50 dark:hover:bg-white/[0.03] transition-colors">
      <span>
        <span class="block text-sm font-semibold text-gray-900 dark:text-white">How these numbers are counted</span>
        <span class="block text-xs text-gray-400 dark:text-gray-500 mt-0.5">What counts, what does not, and where the figures have limits</span>
      </span>
      <svg class="w-4 h-4 flex-shrink-0 text-gray-400 transition-transform duration-200" :class="open ? 'rotate-180' : ''"
        fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
      </svg>
    </button>

    <div v-if="open" class="px-5 pb-5 pt-1 border-t border-gray-100 dark:border-gray-700">
      <div class="overflow-x-auto">
        <table class="w-full text-[13px] mt-3">
          <thead>
            <tr class="text-left text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
              <th class="py-2 pr-4 font-semibold whitespace-nowrap">Figure</th>
              <th class="py-2 pr-4 font-semibold">Counts</th>
              <th class="py-2 font-semibold">Out of</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-800 text-gray-600 dark:text-gray-300 align-top">
            <tr v-for="row in rows" :key="row.name">
              <td class="py-2.5 pr-4 font-medium text-gray-800 dark:text-gray-100 whitespace-nowrap">{{ row.name }}</td>
              <td class="py-2.5 pr-4">{{ row.numerator }}</td>
              <td class="py-2.5">{{ row.denominator }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <ul class="mt-4 space-y-2 text-[13px] text-gray-500 dark:text-gray-400 leading-relaxed">
        <li v-for="note in notes" :key="note" class="flex gap-2">
          <span class="text-gray-300 dark:text-gray-600 flex-shrink-0">•</span>
          <span>{{ note }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'

// Collapsed by default: the casual reader is not taxed, the sceptical one is
// answered without having to open a doc or read the query.
const props = defineProps({
  tab:  { type: String, default: 'bookings' },
  zone: { type: String, default: '' },
})

const open = ref(false)

const BOOKING_ROWS = [
  { name: 'Scheduled', numerator: 'Bookings created inside the period', denominator: 'Excludes the superseded half of a reschedule and payment-rejected attempts' },
  { name: 'Completed', numerator: 'Confirmed meetings whose end time fell inside the period and has passed', denominator: 'Future meetings are never counted, however confirmed' },
  { name: 'Canceled', numerator: 'Bookings canceled inside the period', denominator: 'Dated by the cancellation, not by when the booking was made' },
  { name: 'Rescheduled', numerator: 'Replacement bookings created by a move inside the period', denominator: 'Dated by the move, not by the original booking' },
  { name: 'Cancellation rate', numerator: 'Canceled meetings due to start in the period', denominator: 'All meetings due to start in the period' },
  { name: 'Median notice', numerator: 'Middle value of booking-to-meeting gaps', denominator: 'Bookings created in the period' },
]

const TRAFFIC_ROWS = [
  { name: 'Visitors', numerator: 'Distinct visitor tokens with activity on your pages', denominator: 'One token per browser, set for two years' },
  { name: 'New', numerator: 'Visitors with no visit recorded before the period opened', denominator: 'The same population as Visitors' },
  { name: 'Returning', numerator: 'Visitors already seen before the period opened', denominator: 'The same population as Visitors' },
  { name: 'Conversion', numerator: 'Visitors who completed a booking', denominator: 'All visitors in the period' },
  { name: 'Found no slots', numerator: 'People shown an empty calendar', denominator: 'Everyone who opened a calendar' },
  { name: 'Form rejections', numerator: 'Booking submissions the server rejected', denominator: 'All booking submissions' },
]

const BOOKING_NOTES = [
  'A reschedule creates a second booking row. Counting both would double every moved meeting, so only the replacement is counted.',
  'Bookings on events you have since deleted are still included. The meeting happened, and dropping it would make past totals shrink whenever you tidy up your events.',
  'Cancellations made outside the app, by removing a meeting from a synced calendar, are recorded without an actor and appear as "unattributed".',
]

const TRAFFIC_NOTES = [
  'Visitors are counted by browser cookie, so the same person on a phone and a laptop counts as two, and anyone blocking cookies counts as new on every visit.',
  'Bots are excluded at capture time, and your own visits to your own pages are never counted.',
  'The funnel is measured server side. Someone who opened a booking form and closed it without submitting leaves no trace, so form abandonment is not shown.',
  'Country and city come from the visitor\'s IP address, which is masked before it is stored. City accuracy varies and can be wrong on mobile networks.',
]

const rows = computed(() => (props.tab === 'traffic' ? TRAFFIC_ROWS : BOOKING_ROWS))

const notes = computed(() => {
  const base = props.tab === 'traffic' ? TRAFFIC_NOTES : BOOKING_NOTES
  if (!props.zone) return base
  return [`Days, weekdays and hours are counted in ${props.zone.replace(/_/g, ' ')}, your account timezone, not in UTC.`, ...base]
})
</script>
